// Per-file status row. Renders the queue produced by the upload pipeline.

import type { UploadItem } from "../lib/upload-pipeline";

function statusIcon(s: UploadItem["status"]): string {
  switch (s) {
    case "pending":
      return "○";
    case "uploading":
      return "…";
    case "done":
      return "✓";
    case "error":
      return "✗";
  }
}

function statusColor(s: UploadItem["status"]): string {
  switch (s) {
    case "pending":
      return "text-slate-400";
    case "uploading":
      return "text-sky-600";
    case "done":
      return "text-emerald-600";
    case "error":
      return "text-rose-600";
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function UploadList({ items }: { items: UploadItem[] }): React.ReactElement {
  if (items.length === 0) {
    return <p className="text-sm text-slate-500">No files queued yet.</p>;
  }
  return (
    <ul className="divide-y divide-slate-200 border border-slate-200 rounded-md bg-white">
      {items.map((it) => (
        <li
          key={it.id}
          data-testid="upload-item"
          data-status={it.status}
          className="flex items-center gap-3 px-3 py-2 text-sm"
        >
          <span className={`w-6 text-center font-mono ${statusColor(it.status)}`}>
            {statusIcon(it.status)}
          </span>
          <span className="flex-1 min-w-0">
            <span className="font-medium text-slate-900">{it.filename}</span>
            {it.errorMessage && (
              <span className="block text-xs text-rose-600 truncate" title={it.errorMessage}>
                {it.errorMessage}
              </span>
            )}
          </span>
          <span className="text-xs text-slate-500 tabular-nums">{formatSize(it.size)}</span>
        </li>
      ))}
    </ul>
  );
}
